import { useEffect, useState } from "react";
import { useT } from "@/i18n/LanguageContext";

export default function Navigation() {
    const { lang, setLang, t } = useT();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const links = [
        { href: "#about", label: t.nav.about, id: "about" },
        { href: "#thesis", label: t.nav.thesis, id: "thesis" },
        {
            href: "#collaboration",
            label: t.nav.collaboration,
            id: "collaboration",
        },
        { href: "#contact", label: t.nav.contact, id: "contact" },
    ];

    return (
        <nav
            data-testid="main-nav"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-[#0D0D1A]/85 backdrop-blur-md border-b border-[rgba(201,169,110,0.15)]"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-[1280px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
                <a
                    href="#about"
                    data-testid="nav-logo"
                    aria-label="Alpe.vc — home"
                    className="flex items-center no-underline"
                >
                    <img
                        src="https://customer-assets.emergentagent.com/job_alpine-ventures/artifacts/ukynai28_Color%20logo%20-%20no%20background.png"
                        alt="Alpe.vc"
                        className="h-9 md:h-10 w-auto select-none"
                        draggable="false"
                    />
                </a>

                <div className="hidden md:flex items-center gap-9">
                    {links.map((l) => (
                        <a
                            key={l.id}
                            href={l.href}
                            data-testid={`nav-link-${l.id}`}
                            className="text-[13px] text-[rgba(232,230,223,0.7)] hover:text-[#E8E6DF] transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                    <LangToggle lang={lang} setLang={setLang} />
                    <a
                        href="#contact"
                        data-testid="nav-cta-submit"
                        className="inline-flex items-center justify-center px-[22px] py-[10px] bg-[#C9A96E] text-[#0D0D1A] rounded-[4px] text-[13px] font-medium hover:bg-[#d4b986] transition-colors"
                    >
                        {t.nav.cta}
                    </a>
                </div>

                {/* Mobile toggle */}
                <button
                    type="button"
                    data-testid="nav-mobile-toggle"
                    aria-label="Toggle menu"
                    onClick={() => setOpen((v) => !v)}
                    className="md:hidden text-[#E8E6DF] p-2"
                >
                    <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                    >
                        {open ? (
                            <path
                                strokeLinecap="round"
                                d="M6 6l12 12M18 6L6 18"
                            />
                        ) : (
                            <path
                                strokeLinecap="round"
                                d="M4 7h16M4 12h16M4 17h16"
                            />
                        )}
                    </svg>
                </button>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="md:hidden border-t border-[rgba(201,169,110,0.15)] bg-[#0D0D1A]/95 backdrop-blur-md"
                >
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {links.map((l) => (
                            <a
                                key={l.id}
                                href={l.href}
                                onClick={() => setOpen(false)}
                                className="text-[15px] text-[rgba(232,230,223,0.85)] hover:text-[#C9A96E]"
                            >
                                {l.label}
                            </a>
                        ))}
                        <LangToggle lang={lang} setLang={setLang} />
                        <a
                            href="#contact"
                            onClick={() => setOpen(false)}
                            className="inline-flex items-center justify-center px-[22px] py-[12px] bg-[#C9A96E] text-[#0D0D1A] rounded-[4px] text-[14px] font-medium w-fit"
                        >
                            {t.nav.cta}
                        </a>
                    </div>
                </div>
            )}
        </nav>
    );
}

function LangToggle({ lang, setLang }) {
    const base =
        "px-1 text-[12px] tracking-[0.14em] uppercase transition-colors";
    return (
        <div
            role="group"
            aria-label="Language"
            data-testid="lang-toggle"
            className="flex items-center gap-1 text-[rgba(232,230,223,0.4)]"
        >
            <button
                type="button"
                data-testid="lang-en"
                onClick={() => setLang("en")}
                aria-pressed={lang === "en"}
                className={`${base} ${
                    lang === "en"
                        ? "text-[#C9A96E]"
                        : "hover:text-[#E8E6DF]"
                }`}
            >
                EN
            </button>
            <span aria-hidden="true" className="text-[rgba(201,169,110,0.3)]">
                ·
            </span>
            <button
                type="button"
                data-testid="lang-fr"
                onClick={() => setLang("fr")}
                aria-pressed={lang === "fr"}
                className={`${base} ${
                    lang === "fr"
                        ? "text-[#C9A96E]"
                        : "hover:text-[#E8E6DF]"
                }`}
            >
                FR
            </button>
        </div>
    );
}
